<template>
  <Message v-if="validationError" :closeable="false" severity="error">{{ validationError }}</Message>
  <form @submit="onSubmit" id="report-customization-form">
    <div class="flex flex-col gap-3">
      <div v-if="customizationFieldExists" class="flex flex-row flex-wrap gap-3">
        <StackedLabelDatePicker v-if="props.report.requireAsOf" name="asOfDate" label="As Of Date" :max-date="getMaxDate()" class="w-64!" />
        <StackedLabelDatePicker v-if="props.report.requireDateRange" name="startDate" label="Start Date" :max-date="getMaxDate()" class="w-64!" />
        <StackedLabelDatePicker v-if="props.report.requireDateRange" name="endDate" label="End Date" :max-date="getMaxDate()" class="w-64!" />
        <FormRadioButtonGroup
          v-if="props.report.dateFilterOptions.length > 0"
          name="dateFilterOption"
          :options="props.report.dateFilterOptions"
          label="Filter Date"
        ></FormRadioButtonGroup>
        <StackedLabelInput v-if="props.report.requireKeyword && props.report.keywordTitle" name="keyword" :label="props.report.keywordTitle" />
        <LabeledCheckbox v-if="props.report.checkWaivers" name="includeWaivers" label="Include Waivers" />
      </div>
      <div v-if="props.report.requireProjectStatus" class="flex flex-row gap-5">
        <div class="flex flex-col">
          <p class="font-bold w-fit mt-auto mb-2">Project Status</p>
          <div class="flex flex-row gap-5">
            <LabeledCheckbox
              v-for="statusCategory in ProjectStatusCategoryData"
              :name="statusCategory.name"
              :label="statusCategory.name"
              size="w-fit font-thin"
              class="mt-auto mb-2"
              classes="font-normal ml-2"
            ></LabeledCheckbox>
          </div>
        </div>

        <StackedLabelDatePicker name="soldAfter" label="Sold After" :max-date="getMaxDate()" class="w-64! h-6" />
      </div>
      <Divider></Divider>
      <div v-if="props.report.templates != null && props.report.templates.length > 0" class="flex flex-row w-full gap-2 justify-end">
        <InputGroup class="h-10 w-fit">
          <StackedLabelMultiSelectNoForm
            name="tagFilter"
            v-model="filterTags"
            :options="allTags"
            option-label="name"
            placeholder="Filter Tags"
            label=""
            size="w-fit"
            input-class="w-80 rounded-r-none"
            :disabled="showAll"
            @change="filtersChanged"
          ></StackedLabelMultiSelectNoForm>
          <InputGroupAddon class="w-24">
            <small class="mr-1">Show All</small>
            <Checkbox v-model="showAll" :binary="true" size="small" @value-change="filtersChanged"></Checkbox>
          </InputGroupAddon>
        </InputGroup>
        <Select
          name="templateName"
          :options="filteredTemplates"
          option-label="name"
          v-model="selectedTemplate"
          class="h-10 w-66"
          pt:label:class="pt-2"
        >
        </Select>
      </div>
      <div class="flex flex-row w-full gap-2 justify-end">
        <div v-if="props.report.templates != null && props.report.templates.length > 0">
          <Button
            v-if="customizing"
            label="Templates"
            severity="surface"
            icon="pi pi-cog pi-spin "
            disabled="true"
            iconPos="right"
            class="h-10"
            :onClick="onClickCustomize"
          ></Button>
          <Button v-else label="Templates" severity="surface" icon="pi pi-cog" iconPos="right" class="h-10" :onClick="onClickCustomize"></Button>
        </div>
        <SubmitButton
          :label="launching ? 'Launching...' : 'Launch'"
          :icon="launching ? 'spin pi-spinner' : 'print'"
          class="w-40"
          :disabled="launching"
        ></SubmitButton>
      </div>
    </div>
  </form>
  <TSDialog v-model:visible="customizing" :label="'Customize Report'" size="" icon="cog" v-on:after-hide="$emit('customizedTemplates')">
    <template #content
      ><ReportCustomization v-if="customizing" :report="props.report" :report-options="getReportOptions(null)"></ReportCustomization
    ></template>
  </TSDialog>
</template>

<script setup lang="ts">
import {useForm} from "vee-validate";
import {fileSaveAs} from "@/services/Utility";
import {type ReportData, type ReportOptions, ReportOptionsSchema, type ReportTemplate} from "@/types/Reports";
import {ProjectStatusCategoryData} from "@/enums/ProjectStatusCategory";
import type {Code} from "@/types/Codes";

const emit = defineEmits<{
  customizedTemplates: [];
}>();

const validationError = ref("");
const launching = ref(false);
const customizing = ref(false);
const filterTags = ref<Code[]>([]);
const filteredTemplates = ref<ReportTemplate[]>([]);
const showAll = ref<boolean>(false);

interface Props {
  report: ReportData;
  projectId?: string;
  drawId?: string;
  checkingAccountId?: string;
  subdivisionId?: string;
  allTags: Code[];
  templates: ReportTemplate[];
}
const props = defineProps<Props>();

const selectedTemplate = ref<ReportTemplate | undefined>();
const customizationFieldExists =
  props.report.requireAsOf ||
  props.report.requireDateRange ||
  props.report.requireKeyword ||
  props.report.checkWaivers ||
  props.report.requireProjectStatus;

let initialValues: any = {};
if (props.report.requireAsOf) initialValues.asOfDate = new Date();
if (props.report.requireKeyword) initialValues.keyword = "";
if (props.report.requireProjectStatus) {
  ProjectStatusCategoryData.forEach((statusCategory) => {
    initialValues[statusCategory.name] = true;
  });
}
if (props.report.dateFilterOptions.length > 0) initialValues.dateFilterOption = props.report.dateFilterOptions[0];

const { handleSubmit } = useForm({
  initialValues: initialValues,
  keepValuesOnUnmount: true,
});

const onClickCustomize = function (): void {
  customizing.value = true;
};

const selectDefault = function (): void {
  console.log(selectedTemplate.value);
  if (selectedTemplate.value != null) {
    const oldName: string = selectedTemplate.value.name;
    selectedTemplate.value = props.templates.find((template) => template.name === oldName);
  }
  if (selectedTemplate.value == null) {
    selectedTemplate.value = props.templates.find((template) => template.name === props.report.defaultTemplate);
  }

  if (selectedTemplate.value == null) return;

  const tags: number[] = selectedTemplate.value.tags.split(",").map((tag: string) => parseInt(tag));
  const newTags: Code[] = [];
  filterTags.value.forEach((tag: Code) => {
    if (tags.includes(tag.num)) {
      newTags.push(tag);
    }
  });
  filterTags.value = newTags;
  filtersChanged();
};

const getReportOptions = function (values: any | null): ReportOptions {
  if (values == null) {
    let form = document.getElementById("report-customization-form")! as HTMLFormElement;
    values = new FormData(form);
  }

  let data = ReportOptionsSchema.parse(values);
  if (props.projectId != null) data.projectId = props.projectId;
  if (props.drawId != null) data.drawId = props.drawId;
  if (props.checkingAccountId != null) data.checkingAccountId = props.checkingAccountId;
  if (props.subdivisionId != null) data.subdivisionId = props.subdivisionId;
  if (props.report.templates != null) data.templateName = selectedTemplate.value != null ? selectedTemplate.value.name : "";
  if (props.report.requireProjectStatus) {
    let acceptedStatuses: number[] = [];
    ProjectStatusCategoryData.forEach((statusCategory) => {
      if (values[statusCategory.name]) acceptedStatuses.push(statusCategory.value);
    });
    data.projectStatusList = acceptedStatuses;
  }
  return data;
};

const onSubmit = handleSubmit((values: any): void => {
  if (props.templates.length > 0 && selectedTemplate.value == null) {
    error("Invalid Input", "No report template was selected");
    return;
  }

  let url = "/reports/launch/" + props.report.id;
  let data = getReportOptions(values);

  axios.post(url, data).then((response) => {
    handleApiResponse(
      response,
      (fileName: string) => {
        if (fileName == null || fileName.length == 0) {
          success("There were no results found when running " + props.report.name + ".", "");
        } else {
          success("Opening " + props.report.name + "...", "");
          fileSaveAs("/reports/download/" + fileName, fileName);
        }
        launching.value = false;
      },
      (errors: ApiError[]) => {
        validationError.value = errors[0].message;
        launching.value = false;
      }
    );
  });
  launching.value = true;
});

const getMaxDate = (): Date => {
  let now = new Date();
  let max = new Date();
  max.setFullYear(now.getFullYear() + 10);
  return max;
};

const filtersChanged = (): void => {
  let selectedChanged: boolean = true;
  if (showAll.value) {
    filteredTemplates.value = props.templates;
    if (selectedTemplate.value != null) selectedChanged = false;
  } else {
    filteredTemplates.value = [];
    const filterList: number[] = filterTags.value.map((tag: Code) => tag.num);
    const noTags: boolean = filterList.length == 0;
    props.templates.forEach((template: ReportTemplate) => {
      if (template.name === "<Default Template>") {
        if (noTags) {
          filteredTemplates.value.push(template);
          if (selectedTemplate.value == template) selectedChanged = false;
        }
      } else {
        let tags: number[];
        if (template.tags == null || template.tags === "") tags = [];
        else tags = template.tags.split(",").map((tag: string) => parseInt(tag));
        if (noTags) {
          if (tags.length == 0) {
            filteredTemplates.value.push(template);
            if (selectedTemplate.value == template) selectedChanged = false;
          }
        } else {
          let tagsExist: boolean = true;
          filterList.forEach((filter: number) => {
            if (!tags.includes(filter)) tagsExist = false;
          });
          if (tagsExist) {
            filteredTemplates.value.push(template);
            if (selectedTemplate.value == template) selectedChanged = false;
          }
        }
      }
    });
  }

  if (selectedChanged) {
    selectedTemplate.value = undefined;
    filteredTemplates.value.forEach((temp) => {
      if (temp.name === props.report.defaultTemplate) {
        selectedTemplate.value = temp;
      }
    });
    if (selectedTemplate.value == null) selectedTemplate.value = filteredTemplates.value[0];
  }
};

filtersChanged();

defineExpose({ selectDefault });
</script>
